import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { FaBars, FaTimes, FaSun, FaMoon } from "react-icons/fa";
import { useTranslation } from "../context/useTranslation";
import { useAuth } from "../context/AuthContext";
import { useDarkMode } from "../hooks/useDarkMode";

const Navbar = () => {
  const { isDarkMode, toggleDarkMode } = useDarkMode();
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const isLoggedIn = isAuthenticated();

  const toggleMenu = () => setIsOpen(!isOpen);

  const isActive = (path) => {
    if (path === "/") {
      return location.pathname === "/";
    }
    return location.pathname.startsWith(path);
  };

  return (
    <nav
      className={`fixed w-full z-50 top-0 left-0 shadow-md border-b relative overflow-hidden ${isDarkMode
        ? "bg-gray-900 text-gray-200 border-gray-700"
        : "bg-white text-gray-800 border-gray-200"
        }`}
    >
      <div className="max-w-7xl mx-auto flex justify-between items-center py-6 px-6 md:px-10 h-[80px]">
        {/* Logo */}
        <Link
          to="/"
          className={`text-2xl font-bold tracking-tight transition overflow-hidden ${isDarkMode ? "text-gray-200" : "text-gray-800"
            } hover:text-[#00b300]`}
        >
          {isDarkMode ?
            <img src="/Images/LOGO_DARK.png" alt="Seo Up Rank Logo" className="inline-block object-cover sm:ml-2 h-10 sm:h-15" />
            :
            <img src="/Images/LOGO_LIGHT.png" alt="Seo Up Rank Logo" className="inline-block object-cover sm:ml-2 h-10 sm:h-15 " />
          }
        </Link>

        {/* Desktop Menu */}
        <ul className="hidden lg:flex space-x-8 font-medium">
          {[
            { to: "/", label: t("home") },
            { to: "/about", label: t("aboutUs") },
            { to: "/faqs", label: t("faqs") },
            { to: "/contact", label: t("contactUs") },
          ].map((item, index) => (
            <li key={index}>
              <Link
                to={item.to}
                className={`transition text-lg py-1 ${isActive(item.to)
                    ? "text-[#00b300] font-semibold"
                    : isDarkMode
                      ? "text-gray-200 hover:text-[#00b300]"
                      : "text-gray-700 hover:text-[#00b300]"
                  }`}
              >
                {item.label}
              </Link>
            </li>
          ))}
        </ul>

        {/* Sign In + Dark Mode + Mobile Controls */}
        <div className="flex items-center gap-3">
          <Link
            to={isLoggedIn ? "/dashboard" : "/signin"}
            className="bg-[#00b300] text-white px-4 sm:px-6 py-2 rounded-lg hover:bg-[#009900] transition shadow-md font-medium text-sm sm:text-base"
          >
            {isLoggedIn ? "Dashboard" : t("signIn")}
          </Link>

          <button
            onClick={toggleDarkMode}
            className={`hidden lg:block p-2 rounded-lg border transition ${isDarkMode
              ? "bg-gray-700 text-gray-200 border-gray-600"
              : "bg-white text-gray-800 border-gray-400"
              }`}
            title={isDarkMode ? "Light Mode" : "Dark Mode"}
          >
            {isDarkMode ? <FaSun size={18} /> : <FaMoon size={18} />}
          </button>

          <button
            className={`lg:hidden text-2xl p-2 rounded-lg transition ${
              isDarkMode
                ? "text-gray-200 hover:bg-gray-800"
                : "text-gray-700 hover:bg-gray-200"
            }`}
            onClick={toggleMenu}
          >
            {isOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile/Tablet Sidebar Menu */}
      <div
        className={`lg:hidden fixed top-[80px] right-0 h-[calc(100vh-80px)] w-[60%] sm:w-[55%] md:w-[45%] shadow-2xl transform transition-transform duration-300 ease-in-out z-40 ${
          isOpen ? "translate-x-0" : "translate-x-full"
        } ${isDarkMode
          ? "bg-gray-900 text-gray-200 border-l border-gray-700"
          : "bg-white text-gray-800 border-l border-gray-200"
        }`}
      >
        <div className="flex flex-col h-full px-6 py-8 space-y-6 overflow-y-auto">
          <ul className="flex flex-col space-y-5 font-medium">
            {[
              { to: "/", label: t("home") },
              { to: "/about", label: t("aboutUs") },
              { to: "/faqs", label: t("faqs") },
              { to: "/contact", label: t("contactUs") },
            ].map((item, index) => (
              <li key={index}>
                <Link
                  to={item.to}
                  onClick={toggleMenu}
                  className={`text-lg transition py-2 block ${
                    isActive(item.to)
                      ? "text-[#00b300] font-semibold"
                      : isDarkMode
                      ? "text-gray-300 hover:text-[#00b300]"
                      : "text-gray-700 hover:text-[#00b300]"
                  }`}
                >
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>

          <button
            onClick={toggleDarkMode}
            className={`p-3 rounded-lg border transition font-medium flex items-center justify-center gap-2 ${isDarkMode
              ? "bg-gray-700 text-gray-200 border-gray-600 hover:bg-gray-600"
              : "bg-gray-100 text-gray-800 border-gray-300 hover:bg-gray-200"
            }`}
            title={isDarkMode ? "Light Mode" : "Dark Mode"}
          >
            {isDarkMode ? <FaSun size={18} /> : <FaMoon size={18} />}
            <span>{isDarkMode ? "Light Mode" : "Dark Mode"}</span>
          </button>
        </div>
      </div>

      {isOpen && (
        <div
          className="lg:hidden fixed inset-0 top-[80px] bg-transparent z-30"
          onClick={toggleMenu}
        ></div>
      )}
    </nav>
  );
};

export default Navbar;
